import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/nexus";
import { Loader2, Sparkles, X } from "lucide-react";

const EXAMPLES = [
  "show me drain patterns from LangChain agents on Ethereum",
  "high risk agents in Singapore",
  "OpenAI orchestrators paying more than $1000",
  "critical CrewAI wallets on Solana",
];

export default function NlpQueryBar({ onResult, onClear, active }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setQ(text);
    setLoading(true);
    try {
      const r = await api.post("/graph/query", { query: text });
      onResult(r.data);
    } catch (e) {
      console.error("query error", e);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setQ("");
    onClear?.();
  };

  return (
    <div className="panel brackets p-3">
      <span className="b1" /><span className="b2" />
      <div className="flex items-center gap-2">
        <div className="panel-title shrink-0">
          <span className="dot" style={{ background: "#9D4EDD", boxShadow: "0 0 8px #9D4EDD" }} />
          NL Graph Query
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); run(); }}
          className="flex-1 flex items-center gap-2 border border-nexus-border bg-nexus-bg px-3 py-1.5 focus-within:border-nexus-violet/60"
        >
          <Sparkles size={14} className="text-nexus-violet" />
          <input
            data-testid="nlp-query-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ASK IN PLAIN ENGLISH — e.g. show critical LangChain agents on Ethereum…"
            className="bg-transparent text-xs font-mono uppercase tracking-widest outline-none flex-1 text-nexus-text placeholder:text-nexus-text-3"
          />
          {loading ? (
            <Loader2 size={14} className="animate-spin text-nexus-violet" />
          ) : (
            <button
              type="submit"
              data-testid="nlp-query-submit"
              className="text-[10px] font-mono uppercase tracking-widest text-nexus-violet border border-nexus-violet/40 px-2 py-0.5 hover:bg-nexus-violet/10"
            >
              Run
            </button>
          )}
          {active && (
            <button
              type="button"
              onClick={clear}
              data-testid="nlp-query-clear"
              className="text-nexus-text-3 hover:text-nexus-rose"
              aria-label="Clear query"
            >
              <X size={14} />
            </button>
          )}
        </form>
      </div>

      {active ? (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 flex items-start gap-3 text-[11px]"
        >
          <span className="font-mono uppercase tracking-widest text-nexus-violet shrink-0">Interpretation</span>
          <span className="text-nexus-text-2 flex-1">{active.explanation}</span>
          <span className="font-mono text-nexus-cyan shrink-0">{active.match_count} matches</span>
        </motion.div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">Try:</span>
          {EXAMPLES.map((e) => (
            <button
              key={e}
              data-testid={`nlp-example-${e.slice(0, 20)}`}
              onClick={() => run(e)}
              className="text-[10px] font-mono text-nexus-text-2 border border-nexus-border px-2 py-0.5 hover:text-nexus-cyan hover:border-nexus-cyan/50"
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
